/* ============================================================================
   Ancona Pass 25 — saved-pieces freshness layer (ancona-freshen.js)  [A7]
   The frozen Pass 12/13 Saved dialogs render prices and titles frozen at
   save time, and link to 404s when a product is deleted or drafted. On every
   Saved-dialog open this layer re-validates each stored handle against the
   public /products/{handle}.js endpoint (the same fetch-validate pattern the
   theme already proves in its shortlist viewer):
   - live products: the stored price/title in ancona:v1:saved is refreshed
     (variant-exact when the entry stores ?variant=, "From …" when the product
     has varying prices) and the open dialog row is corrected in place with a
     factual "Price updated" note — no urgency framing, ever;
   - 404 handles: the row is marked "No longer available" beside its existing
     Remove control (network errors are NOT treated as 404 — a piece is only
     declared gone when the shop says so).
   Hooks only the frozen layers' public event (ancona:saved_open) and the PDP
   trigger's click; the frozen files are not modified. Storage shape and cap
   (24) are preserved.
   Feature flag: this <script> tag's data-ancona-freshen attribute (token:
   saved). Absent attribute -> on; empty string -> off.
   Revert: remove the data-ancona-freshen line in layout/theme.liquid + this
   file (row-note styling lives in a marked block in ancona-saved-cart.css).
   ============================================================================ */
(function () {
  'use strict';
  try {
    if (window.__anconaFreshen) return;
    window.__anconaFreshen = true;

    var script = document.currentScript;
    var featAttr = script ? script.getAttribute('data-ancona-freshen') : null;
    /* Absent attribute → on; explicit empty string → off (documented revert path). */
    var FEATURES = (featAttr == null ? 'saved' : featAttr).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (FEATURES.indexOf('saved') === -1) return;

    function track(ev, payload) {
      try {
        if (window.dataLayer && typeof window.dataLayer.push === 'function') window.dataLayer.push(Object.assign({ event: 'ancona:' + ev }, payload || {}));
        document.dispatchEvent(new CustomEvent('ancona:' + ev, { detail: payload || {} }));
      } catch (e) { /* analytics must never break shopping */ }
    }

    var LS = null;
    try { LS = window.localStorage; LS.setItem('ancona:v1:t', '1'); LS.removeItem('ancona:v1:t'); } catch (e) { LS = null; }
    if (!LS) return; /* nothing to freshen without the Saved store */

    var SAVED_KEY = 'ancona:v1:saved'; /* owned by Pass 12 — same shape + cap 24 */
    function savedList() {
      try {
        var v = JSON.parse(LS.getItem(SAVED_KEY));
        return Array.isArray(v) ? v.filter(function (x) { return x && typeof x.h === 'string' && typeof x.u === 'string'; }) : [];
      } catch (e) { try { LS.removeItem(SAVED_KEY); } catch (_) {} return []; }
    }
    function savedWrite(list) { try { LS.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 24))); return true; } catch (e) { return false; } }

    /* Locale/market-safe root (same pattern as ancona-cart-save.js). */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    function rootUrl(path) { return root.replace(/\/$/, '') + '/' + String(path).replace(/^\//, ''); }

    /* Cents → money string in the shop's active currency ("$1,234" / "$1,234.50"). */
    function money(cents) {
      try {
        var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
        var opts = { style: 'currency', currency: cur };
        if (Math.round(cents) % 100 === 0) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
        return new Intl.NumberFormat('en-US', opts).format(cents / 100);
      } catch (e) { return '$' + (cents / 100).toFixed(2); }
    }
    /* Money string → cents (comma-safe), for numeric comparison so a pure
       formatting difference is never reported as a price change. */
    function centsOf(str) {
      var digits = String(str || '').replace(/[^0-9.]/g, '');
      if (!digits) return null;
      var n = parseFloat(digits);
      return isNaN(n) ? null : Math.round(n * 100);
    }

    function openDialogRoot() {
      var dlgs = document.querySelectorAll('.ancona-dlg[role="dialog"]');
      var dlg = dlgs.length ? dlgs[dlgs.length - 1] : null;
      if (!dlg || !/saved pieces/i.test(dlg.getAttribute('aria-label') || '')) return null;
      return dlg;
    }
    function rowFor(dlg, item) {
      var anchors = dlg.querySelectorAll('.ancona-dlg__item > a');
      for (var i = 0; i < anchors.length; i++) {
        if (anchors[i].getAttribute('href') === item.u) return anchors[i].closest('.ancona-dlg__item');
      }
      return null;
    }
    function noteIn(row, cls, text) {
      if (!row) return;
      var meta = row.querySelector('.ancona-dlg__meta') || row;
      if (meta.querySelector('.' + cls)) return; /* idempotent */
      var s = document.createElement('span');
      s.className = cls;
      s.textContent = text;
      meta.appendChild(s);
    }
    function announceIn(dlg, msg) {
      try {
        var body = dlg.querySelector('.ancona-dlg__body') || dlg;
        var lr = body.querySelector('.ancona-freshen-live');
        if (!lr) {
          lr = document.createElement('div');
          lr.className = 'ancona-visually-hidden ancona-freshen-live';
          lr.setAttribute('aria-live', 'polite');
          lr.setAttribute('role', 'status');
          body.appendChild(lr);
        }
        lr.textContent = '';
        window.setTimeout(function () { lr.textContent = msg; }, 30);
      } catch (e) {}
    }

    var running = false;
    function freshen() {
      if (running) return;
      var items = savedList();
      if (!items.length) return;
      running = true;
      var updates = {}; /* handle → {p, t} */
      var gone = {};    /* handle → true (confirmed 404 only) */
      var priceChanged = 0;

      Promise.all(items.map(function (item) {
        return fetch(rootUrl('/products/' + encodeURIComponent(item.h) + '.js'), { headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (r.status === 404) { gone[item.h] = true; return null; }
            return r.ok ? r.json() : null; /* other failures (network, 5xx): skip silently */
          })
          .then(function (p) {
            if (!p) return;
            var cents = null;
            var prefix = '';
            var vm = /[?&]variant=(\d+)/.exec(item.u);
            if (vm && Array.isArray(p.variants)) {
              for (var i = 0; i < p.variants.length; i++) {
                if (String(p.variants[i].id) === vm[1]) { cents = p.variants[i].price; break; }
              }
            }
            if (cents == null) {
              cents = (typeof p.price_min === 'number') ? p.price_min : p.price;
              if (p.price_varies) prefix = 'From ';
            }
            if (typeof cents !== 'number') return;
            var newP = (prefix + money(cents)).slice(0, 24);
            var newT = String(p.title || item.h).slice(0, 80);
            var changedPrice = centsOf(item.p) !== null && centsOf(item.p) !== cents;
            if (newP !== item.p || newT !== item.t) updates[item.h] = { p: newP, t: newT };
            if (changedPrice) { updates[item.h] = updates[item.h] || { p: newP, t: newT }; updates[item.h].changed = true; }
          })
          ['catch'](function () { /* network error — never mark gone, never break the dialog */ });
      })).then(function () {
        running = false;
        /* Write back against a FRESH read: the shopper may have removed items
           while the fetches were in flight. Only existing entries are updated. */
        var fresh = savedList();
        var wrote = false;
        fresh.forEach(function (x) {
          if (updates[x.h]) { x.p = updates[x.h].p; x.t = updates[x.h].t; wrote = true; }
        });
        if (wrote) savedWrite(fresh);

        /* Correct the open dialog in place (if it is still open). */
        var dlg = openDialogRoot();
        var goneCount = 0;
        items.forEach(function (item) {
          if (gone[item.h]) {
            goneCount++;
            if (dlg) {
              var row = rowFor(dlg, item);
              if (row) {
                row.classList.add('ancona-freshen--gone');
                noteIn(row, 'ancona-freshen__gone', 'No longer available');
                /* The row's existing Remove control stays — that is the exit.
                   Disable a Pass 25 Add-to-cart button if one was injected. */
                var add = row.querySelector('.ancona-savedcart__add');
                if (add) { add.disabled = true; add.textContent = 'Unavailable'; }
              }
            }
            return;
          }
          var up = updates[item.h];
          if (!up || !dlg) return;
          var row2 = rowFor(dlg, item);
          if (!row2) return;
          var tEl = row2.querySelector('.ancona-dlg__t');
          if (tEl && up.t) tEl.textContent = up.t;
          var pEl = row2.querySelector('.ancona-dlg__p');
          if (pEl) pEl.textContent = up.p;
          else if (up.p) {
            var meta = row2.querySelector('.ancona-dlg__meta');
            if (meta) { pEl = document.createElement('span'); pEl.className = 'ancona-dlg__p'; pEl.textContent = up.p; meta.appendChild(pEl); }
          }
          if (up.changed) { noteIn(row2, 'ancona-freshen__note', 'Price updated'); priceChanged++; }
        });

        if (dlg && (priceChanged || goneCount)) {
          var parts = [];
          if (priceChanged) parts.push(priceChanged === 1 ? 'One saved price was updated.' : priceChanged + ' saved prices were updated.');
          if (goneCount) parts.push(goneCount === 1 ? 'One saved piece is no longer available.' : goneCount + ' saved pieces are no longer available.');
          announceIn(dlg, parts.join(' '));
        }
        if (priceChanged || goneCount) track('saved_refresh', { updated: priceChanged, gone: goneCount });
      })['catch'](function () { running = false; });
    }

    function onOpen() {
      /* Defer one tick so the (synchronously built) dialog is in the DOM
         before rows are matched (same pattern as ancona-deluxe.js). */
      window.setTimeout(function () { try { freshen(); } catch (e) { running = false; } }, 0);
    }

    function boot() {
      document.addEventListener('ancona:saved_open', onOpen);
      var savedTrigger = document.getElementById('AnconaSavedOpen');
      if (savedTrigger) savedTrigger.addEventListener('click', onOpen);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (e) { /* the freshness layer must never break the page */ }
})();
